import React from 'react';
import {
  Linkedin,
  MailIcon,
  CodeIcon,
  LayoutIcon,
  GlobeIcon,
  ShieldIcon,
  RocketIcon,
  BrainCircuitIcon,
  CompassIcon,
  ArrowUpRight,
  CheckIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Nav } from '@/components/Nav';
import { Footer } from '@/components/Footer';
import { Reveal } from '@/components/Reveal';
import { cn } from '@/lib/utils';

interface Project {
  title: string;
  description: string;
  tech: string[];
  category: 'mobile' | 'backend' | 'fullstack';
}

interface Service {
  title: string;
  description: string;
  icon: React.ReactNode;
  details: string[];
}

const projects: Project[] = [
  {
    title: 'studii',
    description:
      'An AI education app that generates customizable, recursive lessons on any topic — drill into any concept to expand it into its own lesson.',
    tech: ['React', 'TypeScript', 'Go', 'Gin', 'PostgreSQL', 'LLMs'],
    category: 'fullstack',
  },
  {
    title: 'Buzzz',
    description:
      'An all-in-one email platform — send transactional email via API, build marketing campaigns, and track delivery, opens, and clicks from one dashboard.',
    tech: ['Next.js', 'TypeScript', 'Prisma', 'PostgreSQL', 'BullMQ', 'Redis'],
    category: 'fullstack',
  },
  {
    title: 'Enterprise Payment Platform',
    description:
      'A secure payment and escrow platform enabling small businesses to manage transactions safely across emerging markets.',
    tech: ['Go', 'React', 'TypeScript', 'PostgreSQL', 'Docker'],
    category: 'fullstack',
  },
  {
    title: 'AI-Powered Content Platform',
    description:
      'A content recommendation system using LLMs and vector search for personalized content discovery.',
    tech: ['Astro', 'LangChain', 'Pinecone', 'OpenAI', 'AWS'],
    category: 'fullstack',
  },
  {
    title: 'Fintech Money Transfer Platform',
    description:
      'Led development of a cross-border money transfer platform and anti-money laundering system.',
    tech: ['Kotlin', 'Retrofit', 'Java', 'Docker', 'MySQL'],
    category: 'fullstack',
  },
  {
    title: 'Social Commerce Video Platform',
    description:
      'A TikTok-style video feed feature for a European e-commerce platform.',
    tech: ['Android', 'Jetpack Compose', 'GraphQL', 'ExoPlayer', 'Mux'],
    category: 'mobile',
  },
  {
    title: 'Luxury E-commerce App',
    description:
      'Native iOS shopping application for a high-end fashion retailer.',
    tech: ['iOS', 'SwiftUI', 'Firebase', 'Fastlane'],
    category: 'mobile',
  },
  {
    title: 'Financial Services SDK',
    description:
      'Authentication SDK enabling secure access across banking applications.',
    tech: ['Android', 'iOS', 'Security', 'Fastlane', 'Groovy'],
    category: 'mobile',
  },
];

const services: Service[] = [
  {
    title: 'AI Engineering',
    description:
      'Production AI systems — from RAG pipelines to LLM-powered product features.',
    icon: <BrainCircuitIcon className="h-6 w-6" />,
    details: [
      'RAG & Vector Search',
      'LLM-Powered Applications',
      'AI Agents & Automation',
      'Model Integration & Evaluation',
    ],
  },
  {
    title: 'Payment Systems',
    description:
      'Secure payment infrastructure with escrow capabilities for emerging markets.',
    icon: <ShieldIcon className="h-6 w-6" />,
    details: [
      'Escrow Payment Processing',
      'Fraud Prevention',
      'Multi-currency Support',
      'Compliance & Security',
    ],
  },
  {
    title: 'Mobile App Development',
    description:
      'Native iOS and Android applications built with modern frameworks and best practices.',
    icon: <GlobeIcon className="h-6 w-6" />,
    details: [
      'Native Android (Kotlin/Java)',
      'Native iOS (Swift)',
      'Mobile DevOps',
      'UI/UX Implementation',
    ],
  },
  {
    title: 'SDK Development',
    description:
      'Custom SDKs that enable seamless integration of complex functionality.',
    icon: <CodeIcon className="h-6 w-6" />,
    details: [
      'Authentication Systems',
      'Payment Integration',
      'Analytics',
      '3rd Party SDKs',
    ],
  },
  {
    title: 'Backend Development',
    description:
      'Scalable and secure backend services that power modern applications.',
    icon: <LayoutIcon className="h-6 w-6" />,
    details: [
      'API Development',
      'Database Design',
      'Cloud Infrastructure',
      'Security Implementation',
    ],
  },
  {
    title: 'Product Development',
    description: 'End-to-end product development from ideation to market launch.',
    icon: <RocketIcon className="h-6 w-6" />,
    details: [
      'Market Research & Analysis',
      'MVP Development',
      'Product Strategy',
      'User Testing & Iteration',
    ],
  },
  {
    title: 'Tech Strategy',
    description:
      'Strategic technology planning and implementation for business growth.',
    icon: <CompassIcon className="h-6 w-6" />,
    details: [
      'Technology Roadmapping',
      'Architecture Planning',
      'Team Structure & Growth',
      'Cost Optimization',
    ],
  },
];

const achievements = [
  'Built an escrow payment platform processing secure transactions across Southern Africa.',
  'Led development of mobile SDKs used by companies across Africa and Europe.',
  'Architected authentication systems securing millions of user accounts.',
  'Developed e-commerce platforms processing $10M+ in annual transactions.',
  'Mentored junior developers and led technical teams across multiple projects.',
  'Increased user engagement by 20% with a social commerce video platform.',
];

const stats = [
  { value: '17+', label: 'Years building' },
  { value: '$10M+', label: 'Processed annually' },
  { value: 'iOS · Android · Go', label: 'Core stack' },
];

interface Flagship {
  eyebrow: string;
  title: string;
  description: string;
  features: string[];
  tech: string[];
  href: string;
  image: string;
  imageAlt: string;
}

const flagships: Flagship[] = [
  {
    eyebrow: 'Flagship · PG Forge',
    title: 'A self-hosted platform for shipping apps.',
    description:
      'Provision PostgreSQL, Redis, and S3 storage, then deploy Docker apps with automatic subdomains and SSL. Includes AI-powered deploys — point it at a repo and an LLM generates the deployment plan — plus a built-in MCP server and a full CLI.',
    features: [
      'AI-powered deploys',
      'Built-in MCP server',
      'Auto SSL & subdomains',
      'CLI + REST API',
    ],
    tech: ['Go', 'React 19', 'Docker', 'Caddy', 'PostgreSQL', 'LLMs'],
    href: 'https://forge.beesworx.co.za',
    image: '/forge-screenshot.png',
    imageAlt: 'PG Forge landing page',
  },
  {
    eyebrow: 'Flagship · Nectrr',
    title: 'AI meal planning for lasting weight management.',
    description:
      'A consumer health app that builds personalized meal plans, tracks goals, and coaches habits with behavioral psychology — pairing a dietitian-approved recipe library, a calorie calculator, and smart shopping lists in one warm, polished experience.',
    features: [
      'AI meal plans',
      'Goal-based tracking',
      'Psychology-based coaching',
      'Smart shopping lists',
    ],
    tech: ['Next.js', 'React', 'TypeScript', 'Tailwind', 'LLMs', 'Netlify'],
    href: 'https://nectrr.co.za',
    image: '/nectrr-screenshot.png',
    imageAlt: 'Nectrr landing page',
  },
];

function FlagshipCard({ flagship, index }: { flagship: Flagship; index: number }) {
  const imageLeft = index % 2 === 1;
  return (
    <Reveal className="mb-12">
      <div className="grid overflow-hidden rounded-2xl border border-border bg-card lg:grid-cols-2">
        <div
          className={cn(
            'flex flex-col justify-center gap-5 p-8 sm:p-10',
            imageLeft && 'lg:order-2'
          )}
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
            {flagship.eyebrow}
          </p>
          <h3 className="font-serif text-3xl font-semibold tracking-tight sm:text-4xl">
            {flagship.title}
          </h3>
          <p className="leading-relaxed text-muted-foreground">
            {flagship.description}
          </p>
          <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {flagship.features.map((feature) => (
              <li
                key={feature}
                className="flex items-center gap-2.5 text-sm text-muted-foreground"
              >
                <CheckIcon className="h-4 w-4 shrink-0 text-primary" />
                {feature}
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-2">
            {flagship.tech.map((tech) => (
              <Tag key={tech}>{tech}</Tag>
            ))}
          </div>
          <div className="pt-1">
            <Button asChild className="rounded-full">
              <a href={flagship.href} target="_blank" rel="noopener noreferrer">
                Visit live site
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>

        <a
          href={flagship.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Visit ${flagship.title}`}
          className={cn(
            'group relative min-h-64 overflow-hidden border-t border-border bg-muted lg:border-t-0',
            imageLeft ? 'lg:order-1 lg:border-r' : 'lg:border-l'
          )}
        >
          <img
            src={flagship.image}
            alt={flagship.imageAlt}
            loading="lazy"
            className="h-full w-full object-cover object-left-top transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </a>
      </div>
    </Reveal>
  );
}

function Eyebrow({ index, children }: { index: string; children: string }) {
  return (
    <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
      <span className="text-muted-foreground">{index}</span>
      <span className="mx-2 text-border">—</span>
      {children}
    </p>
  );
}

function SectionHeading({
  index,
  eyebrow,
  title,
  subtitle,
}: {
  index: string;
  eyebrow: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <Reveal className="mb-12 max-w-2xl space-y-4">
      <Eyebrow index={index}>{eyebrow}</Eyebrow>
      <h2 className="font-serif text-4xl font-semibold tracking-tight sm:text-5xl">
        {title}
      </h2>
      {subtitle && (
        <p className="text-lg text-muted-foreground text-balance">{subtitle}</p>
      )}
    </Reveal>
  );
}

function Tag({ children }: { children: string }) {
  return (
    <span className="rounded-full border border-border px-3 py-1 font-mono text-xs text-muted-foreground">
      {children}
    </span>
  );
}

const tabClasses = {
  list: 'h-auto justify-start gap-6 rounded-none border-b border-border bg-transparent p-0',
  trigger:
    'rounded-none border-b-2 border-transparent bg-transparent px-0 pb-3 font-medium text-muted-foreground shadow-none transition-colors data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none',
};

function ProjectGrid({ items }: { items: Project[] }) {
  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {items.map((project, i) => (
        <Reveal key={project.title} delay={(i % 3) * 80}>
          <article className="group flex h-full flex-col rounded-xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5">
            <div className="mb-3 flex items-start justify-between gap-3">
              <h3 className="font-serif text-xl font-semibold tracking-tight">
                {project.title}
              </h3>
              <ArrowUpRight className="h-5 w-5 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
            </div>
            <p className="mb-6 flex-1 text-sm leading-relaxed text-muted-foreground">
              {project.description}
            </p>
            <div className="flex flex-wrap gap-2">
              {project.tech.map((tech) => (
                <Tag key={tech}>{tech}</Tag>
              ))}
            </div>
          </article>
        </Reveal>
      ))}
    </div>
  );
}

const Portfolio = () => {
  return (
    <div id="top" className="min-h-screen bg-background">
      <Nav />

      <main>
        {/* Hero */}
        <section className="mx-auto max-w-5xl px-6 pb-24 pt-40 sm:pt-48">
          <Reveal className="space-y-8">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
              Full Stack &amp; AI Engineer
            </p>
            <h1 className="max-w-4xl font-serif text-5xl font-semibold leading-[1.05] tracking-tight text-balance sm:text-7xl">
              Building secure payment systems, AI solutions, and software that
              scales.
            </h1>
            <p className="max-w-2xl text-lg leading-relaxed text-muted-foreground">
              I&apos;m Brian Machimbira — 17+ years building robust
              applications, payment platforms, and AI systems. Expert in native
              Android &amp; iOS, Go, React, RAG pipelines, and cloud
              infrastructure.
            </p>
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button size="lg" asChild className="rounded-full">
                <a href="#contact">
                  Get in touch
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </Button>
              <Button
                variant="outline"
                size="icon"
                asChild
                className="rounded-full"
              >
                <a
                  href="https://www.linkedin.com/in/brianmachimbira/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
              </Button>
              <Button
                variant="outline"
                size="icon"
                asChild
                className="rounded-full"
              >
                <a href="mailto:brian@brianmachimbira.dev" aria-label="Email">
                  <MailIcon className="h-5 w-5" />
                </a>
              </Button>
            </div>
          </Reveal>

          <Reveal
            delay={120}
            className="mt-16 grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-3"
          >
            {stats.map((stat) => (
              <div key={stat.label} className="bg-card p-6">
                <p className="font-serif text-2xl font-semibold tracking-tight">
                  {stat.value}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {stat.label}
                </p>
              </div>
            ))}
          </Reveal>
        </section>

        {/* Services */}
        <section id="services" className="mx-auto max-w-5xl px-6 py-24">
          <SectionHeading
            index="01"
            eyebrow="Services"
            title="What I do"
            subtitle="From first line of code to launch — and the strategy that surrounds it."
          />
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {services.map((service, i) => (
              <Reveal key={service.title} delay={(i % 3) * 80}>
                <div className="group flex h-full flex-col rounded-xl border border-border bg-card p-6 transition-colors hover:border-primary/40">
                  <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-full bg-accent text-primary transition-transform duration-300 group-hover:scale-105">
                    {service.icon}
                  </div>
                  <h3 className="font-serif text-xl font-semibold tracking-tight">
                    {service.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {service.description}
                  </p>
                  <ul className="mt-5 space-y-2.5 border-t border-border pt-5">
                    {service.details.map((detail) => (
                      <li
                        key={detail}
                        className="flex items-center gap-2.5 text-sm text-muted-foreground"
                      >
                        <CheckIcon className="h-4 w-4 shrink-0 text-primary" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Work */}
        <section id="work" className="mx-auto max-w-5xl px-6 py-24">
          <SectionHeading
            index="02"
            eyebrow="Selected Work"
            title="Featured projects"
            subtitle="A sample of platforms and products shipped across fintech, health, commerce, and mobile."
          />

          <div className="mb-4 space-y-4">
            {flagships.map((flagship, i) => (
              <FlagshipCard key={flagship.href} flagship={flagship} index={i} />
            ))}
          </div>

          <h3 className="mb-8 mt-16 font-serif text-2xl font-semibold tracking-tight">
            More work
          </h3>
          <Tabs defaultValue="all">
            <TabsList className={tabClasses.list}>
              <TabsTrigger value="all" className={tabClasses.trigger}>
                All
              </TabsTrigger>
              <TabsTrigger value="fullstack" className={tabClasses.trigger}>
                Full Stack
              </TabsTrigger>
              <TabsTrigger value="mobile" className={tabClasses.trigger}>
                Mobile
              </TabsTrigger>
            </TabsList>
            <TabsContent value="all" className="mt-10">
              <ProjectGrid items={projects} />
            </TabsContent>
            {(['fullstack', 'mobile'] as const).map((category) => (
              <TabsContent key={category} value={category} className="mt-10">
                <ProjectGrid
                  items={projects.filter((p) => p.category === category)}
                />
              </TabsContent>
            ))}
          </Tabs>
        </section>

        {/* About / Track record */}
        <section id="about" className="mx-auto max-w-5xl px-6 py-24">
          <SectionHeading
            index="03"
            eyebrow="Track Record"
            title="Selected achievements"
            subtitle="Outcomes that have shipped to real users across Africa and Europe."
          />
          <div className="grid gap-x-12 gap-y-px overflow-hidden sm:grid-cols-2">
            {achievements.map((achievement, i) => (
              <Reveal key={achievement} delay={(i % 2) * 80}>
                <div className="flex items-start gap-4 border-t border-border py-6">
                  <span className="font-mono text-sm text-primary">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <p className="text-base leading-relaxed">{achievement}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="mx-auto max-w-5xl px-6 py-24">
          <Reveal className="overflow-hidden rounded-3xl border border-border bg-card px-6 py-20 text-center">
            <Eyebrow index="04">Contact</Eyebrow>
            <h2 className="mx-auto mt-5 max-w-2xl font-serif text-4xl font-semibold tracking-tight text-balance sm:text-5xl">
              Let&apos;s build something great.
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-lg text-muted-foreground">
              Looking to build a secure, scalable application? Let&apos;s discuss
              how I can help bring your vision to life.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Button size="lg" asChild className="rounded-full">
                <a href="mailto:brian@brianmachimbira.dev">
                  Get in touch
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </Button>
              <Button
                variant="outline"
                size="lg"
                asChild
                className="rounded-full"
              >
                <a
                  href="https://www.linkedin.com/in/brianmachimbira/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Linkedin className="h-4 w-4" />
                  LinkedIn
                </a>
              </Button>
            </div>
          </Reveal>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Portfolio;
